/**
 * Centralized Events Configuration
 * 
 * This file defines all wedding events with their properties and enabled state.
 * To show/hide events, simply toggle the `enabled` property.
 * 
 * Usage:
 * - Schedule section will only render enabled events
 * - RSVP form will only show enabled events as options
 * - Admin dashboard will respect enabled events
 */

export interface EventConfig {
  key: 'engagement' | 'haldi' | 'wedding';
  enabled: boolean;
  // Schedule section properties
  title: string;
  date: string;
  city: string;
  description: string;
  venue: string;
  schedule: readonly string[];
  includes: string;
  directionsUrl: string;
  // RSVP section properties
  rsvpLabel: string;
}

/**
 * Master Events Configuration
 * Toggle the `enabled` property to show/hide events throughout the app
 */
export const EVENTS_CONFIG: Record<string, EventConfig> = {
  engagement: {
    key: 'engagement',
    enabled: true,
    // Schedule section data
    title: 'Engagement & Sangeet',
    date: '28th December, 2025',
    city: 'Pune',
    description: 'A celebration of love, commitment, and melodies that mark a new beginning.',
    venue: 'At Asodit Banquets, Magarpatta Rd, Near Seasons Mall, Pune',
    schedule: ['6:00 PM Onwards'],
    includes: 'Come hungry! Dinner will be served with love',
    directionsUrl: 'https://maps.app.goo.gl/FPLG4JUHxTQUvc356',
    // RSVP section data
    rsvpLabel: 'Engagement & Sangeet (Pune, 28th Dec)',
  },

  sangeet: {
    key: 'haldi',
    enabled: true,
    // Schedule section data
    title: 'Haldi',
    date: '29th December, 2025',
    city: 'Pune',
    description: 'Blessed in yellow, glowing with love 💛',
    venue: 'At Asodit Banquets, Magarpatta Rd, Near Seasons Mall, Pune',
    schedule: ['8:00 AM Onwards'],
    includes: 'Come hungry - breakfast is on us!',
    directionsUrl: 'https://maps.app.goo.gl/FPLG4JUHxTQUvc356',
    // RSVP section data
    rsvpLabel: 'Haldi (Pune, 29th Dec)',
  },

  wedding: {
    key: 'wedding',
    enabled: true,
    // Schedule section data
    title: 'Wedding',
    date: '29th December, 2025',
    city: 'Pune',
    description: 'From Ahmdabad to Pune, we\'re closing the distance with vows and turning miles into memories!',
    venue: 'At Asodit Banquets, Magarpatta Rd, Near Seasons Mall, Pune',
    schedule: ['12:00 PM Onwards'],
    includes: 'We\'ll end the afternoon with lunch - good food, laughter, and love all around.',
    directionsUrl: 'https://maps.app.goo.gl/FPLG4JUHxTQUvc356',
    // RSVP section data
    rsvpLabel: 'Wedding (Pune, 29th Dec)',
  },
} as const;

/**
 * Helper functions to work with events configuration
 */
export const getEnabledEvents = () => {
  return Object.values(EVENTS_CONFIG).filter(event => event.enabled);
};

export const getEventByKey = (key: string) => {
  return EVENTS_CONFIG[key];
};

export const getEnabledEventKeys = () => {
  return Object.keys(EVENTS_CONFIG).filter(key => EVENTS_CONFIG[key].enabled);
};

export const getEnabledEventsForRSVP = () => {
  return getEnabledEvents().map(event => ({
    key: event.key,
    label: event.rsvpLabel,
  }));
};

export const getEnabledEventsForSchedule = () => {
  return getEnabledEvents().map(event => ({
    key: event.key,
    title: event.title,
    date: event.date,
    city: event.city,
    description: event.description,
    venue: event.venue,
    schedule: event.schedule,
    includes: event.includes,
    directionsUrl: event.directionsUrl,
  }));
};

/**
 * Get accommodation options based on enabled events
 * - Pune option shows if both sangeet and wedding are enabled
 * - Mumbai option shows if finalSoiree is enabled
 */
export const getEnabledAccommodationOptions = () => {
  const options: Array<{value: string, label: string}> = [];
  
  // Show Pune accommodation if both Pune events (sangeet and wedding) are enabled
  const sangeetEnabled = EVENTS_CONFIG.sangeet.enabled;
  const weddingEnabled = EVENTS_CONFIG.wedding.enabled;
  
  if (sangeetEnabled && weddingEnabled) {
    options.push({
      value: 'pune',
      label: 'Pune - Haldi, Sangeet & Wedding (28th Night)',
    });
  }
  
  return options;
};

/**
 * Location-based filtering functions
 */
//export type LocationFilter = 'all' | 'pune' | 'mumbai' | 'main';

export type LocationFilter = 'all' | 'main';


export const getEventsByLocation = (location: LocationFilter) => {
  const enabledEvents = getEnabledEvents();
  
  if (location === 'all') {
    return enabledEvents;
  }
  
      return enabledEvents.filter(event => event.key !== 'engagement');
};

export const getEventsForRSVPByLocation = (location: LocationFilter) => {
  return getEventsByLocation(location).map(event => ({
    key: event.key,
    label: event.rsvpLabel,
  }));
};

export const getEventsForScheduleByLocation = (location: LocationFilter) => {
  return getEventsByLocation(location).map(event => ({
    key: event.key,
    title: event.title,
    date: event.date,
    city: event.city,
    description: event.description,
    venue: event.venue,
    schedule: event.schedule,
    includes: event.includes,
    directionsUrl: event.directionsUrl,
  }));
};

export const getAccommodationOptionsByLocation = (location: LocationFilter) => {
  const allOptions = getEnabledAccommodationOptions();
  
  if (location === 'all' || location === 'main') {
    // Both 'all' and 'main' show all accommodation options since main events span both cities
    return allOptions;
  }
  
  // Filter accommodation options by location
  return allOptions.filter(option => 
    option.value === location
  );
};